/**
 * Attribution Tracking Utility
 *
 * Captures UTM parameters and referrer data on first visit,
 * persists them through the signup flow, and writes to Supabase
 * when the user completes registration.
 *
 * Also provides funnel event tracking via trackFunnelEvent() and
 * cookie-based anonymous ID for pre-auth session stitching.
 *
 * Usage:
 *   - Call captureAttribution() on app load (in App.tsx)
 *   - Call saveAttribution(userId) after successful signup (in AuthContext)
 *   - Call getStoredAttribution() to read current attribution data
 *   - Call trackFunnelEvent(eventType, metadata?) to log funnel events
 *   - Call setLandingVariant(variant) from the Landing component
 */

import { supabase } from '@/lib/supabase';
import { reportError } from '@/services/errorTracking';
import { hasAnalyticsConsent, readConsent } from '@/hooks/useConsent';
import { randomUUID } from '@/utils/uuid';
import {
  approvedCampaignTags,
  withCampaignTags,
  type CampaignLabelKey,
} from '@/utils/campaignLabels';

// --------------------------------------------------------------------------
// Anonymous ID & Device Helpers
// --------------------------------------------------------------------------

/**
 * Persistent anonymous id for analytics only.
 * Undecided or declined consent returns null and does not read or write vidi_aid.
 */
function getAnonymousId(): string | null {
  if (!hasAnalyticsConsent()) return null;

  const match = document.cookie.match(/vidi_aid=([^;]+)/);
  if (match) return match[1];

  const id = randomUUID();
  const maxAge = 365 * 24 * 60 * 60; // 1 year
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `vidi_aid=${id}; max-age=${maxAge}; path=/; SameSite=Lax${secure}`;
  return id;
}

function getDeviceType(): string {
  const ua = navigator.userAgent.toLowerCase();
  if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return 'mobile';
  return 'desktop';
}

function getReferrerDomain(): string | null {
  if (!document.referrer) return null;
  try {
    const hostname = new URL(document.referrer).hostname;
    if (hostname === window.location.hostname) return null;
    return hostname;
  } catch {
    return null;
  }
}

// --------------------------------------------------------------------------
// Types
// --------------------------------------------------------------------------

export interface AttributionData {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  referrer: string | null;
  landing_page: string | null;
  first_seen_at: string; // ISO timestamp
  referral_code: string | null;
  anonymous_id: string;
  device_type: string;
  referrer_domain: string | null;
  landing_variant: string | null;
}

// --------------------------------------------------------------------------
// Constants
// --------------------------------------------------------------------------

const STORAGE_KEY = 'vidi_attribution';
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

// --------------------------------------------------------------------------
// Core Functions
// --------------------------------------------------------------------------

/**
 * Landing-URL facts held in memory (never persisted, never sent) while the
 * visitor has not yet answered the cookie banner.
 *
 * Why: the banner is shown AFTER the first page load, so on a first visit
 * captureAttribution() always runs with consent still undecided. It used to
 * strip the UTM parameters from the URL and return, which threw away the
 * campaign for every first-time visitor (production had 0 attribution rows
 * with a utm_campaign out of 3,248 on 2026-09-29). Holding the values in a
 * module variable keeps them for the life of the SPA session without writing
 * to cookies/storage or contacting a server, so nothing is processed before
 * consent (TDDDG Section 25). They are used only if the visitor accepts
 * analytics, and dropped if they choose essential-only.
 */
interface PendingLanding {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  referral_code: string | null;
  landing_page: string;
  referrer: string | null;
  seenAt: string;
}

let pendingLanding: PendingLanding | null = null;

/** Test hook: forget any in-memory landing snapshot. */
export function _resetPendingLandingForTests(): void {
  pendingLanding = null;
}

/**
 * sessionStorage can throw on access (blocked storage, some private modes).
 * Attribution is best effort and must never take the page down, so every
 * touch of it goes through these three helpers.
 */
function readStoredRaw(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredRaw(value: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* blocked storage: attribution is simply not kept */
  }
}

function removeStoredRaw(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* blocked storage: nothing to remove */
  }
}

/**
 * Make the address bar safe to keep, whatever the consent state or stored
 * first touch. Keeps the approved campaign labels (navigation context, see
 * campaignLabels.ts) and removes utm_term, ref and any unapproved utm value.
 * Only the utm/ref parameters are touched; other parameters stay as they are.
 */
function sanitizeLandingUrl(): void {
  const url = new URL(window.location.href);
  const params = url.searchParams;
  if (!UTM_PARAMS.some((param) => params.has(param)) && !params.has('ref')) return;

  const tags = approvedCampaignTags(params);
  for (const param of UTM_PARAMS) {
    const approved = tags[param as CampaignLabelKey];
    // set() on an existing key keeps its position, so the link reads as it was written.
    if (approved === undefined) params.delete(param);
    else params.set(param, approved);
  }
  params.delete('ref');

  const cleanUrl = url.pathname + (params.toString() ? '?' + params.toString() : '') + url.hash;
  if (cleanUrl !== window.location.pathname + window.location.search + window.location.hash) {
    // Keep the router's own history.state; replacing it with {} would drop its entry index.
    window.history.replaceState(window.history.state, '', cleanUrl);
  }
}

/**
 * Capture attribution data from the current URL and referrer.
 *
 * Call this on app load AND whenever the consent decision changes. It only
 * writes to sessionStorage once consent is granted, and only on the FIRST
 * visit (first-touch attribution). Subsequent page navigations won't
 * overwrite the original attribution.
 *
 * The URL is sanitized on every call, including when a first touch is already
 * stored or storage is blocked: what may stay in the address bar does not
 * depend on what was captured.
 */
export function captureAttribution(): void {
  try {
    captureFromLanding();
  } finally {
    sanitizeLandingUrl();
  }
}

function captureFromLanding(): void {
  // Don't overwrite existing attribution (first-touch model)
  if (readStoredRaw()) {
    return;
  }

  const url = new URL(window.location.href);
  const params = url.searchParams;
  const approvedTags = approvedCampaignTags(params);
  const safeLandingPage = withCampaignTags(url.pathname);
  const hasUtmParams = UTM_PARAMS.some((param) => params.has(param));
  const refCode = params.get('ref');
  const hasReferrer = document.referrer && !document.referrer.includes(window.location.hostname);

  // Gate UTM capture and session tracking behind analytics consent (TDDDG Section 25)
  if (!hasAnalyticsConsent()) {
    if (readConsent() === null) {
      // Undecided (first visit): remember the landing facts in memory so a
      // later "accept" can still attribute this visit. A visit that carries
      // no campaign data must not overwrite an earlier snapshot.
      if (hasUtmParams || refCode || !pendingLanding) {
        pendingLanding = {
          utm_source: approvedTags.utm_source ?? null,
          utm_medium: approvedTags.utm_medium ?? null,
          utm_campaign: approvedTags.utm_campaign ?? null,
          utm_content: approvedTags.utm_content ?? null,
          utm_term: null,
          referral_code: refCode,
          landing_page: safeLandingPage,
          referrer: hasReferrer ? document.referrer : null,
          seenAt: new Date().toISOString(),
        };
      }
    } else {
      // Explicitly declined: drop anything held and store nothing.
      pendingLanding = null;
    }
    // Nothing is stored or sent here; captureAttribution() sanitizes the URL after.
    return;
  }

  // Consent granted: the unanswered first landing remains first touch, including
  // its referral code. An explicit decline erased this snapshot before this point.
  const held = pendingLanding;
  pendingLanding = null;
  const useHeld = held !== null;

  const anonymousId = getAnonymousId();
  if (!anonymousId) return;

  const attribution: AttributionData = {
    utm_source: useHeld ? held.utm_source : (approvedTags.utm_source ?? null),
    utm_medium: useHeld ? held.utm_medium : (approvedTags.utm_medium ?? null),
    utm_campaign: useHeld ? held.utm_campaign : (approvedTags.utm_campaign ?? null),
    utm_content: useHeld ? held.utm_content : (approvedTags.utm_content ?? null),
    utm_term: null,
    referrer: useHeld ? held.referrer : hasReferrer ? document.referrer : null,
    landing_page: useHeld ? held.landing_page : safeLandingPage,
    first_seen_at: useHeld ? held.seenAt : new Date().toISOString(),
    referral_code: useHeld ? held.referral_code : refCode,
    anonymous_id: anonymousId,
    device_type: getDeviceType(),
    referrer_domain: getReferrerDomain(),
    landing_variant: null,
  };

  // Infer source from referrer if no UTM source is set
  if (!attribution.utm_source && hasReferrer) {
    attribution.utm_source = inferSourceFromReferrer(document.referrer);
    attribution.utm_medium = 'referral';
  }

  writeStoredRaw(JSON.stringify(attribution));

  // Fire session tracking event (fire-and-forget). Authorization is attached
  // only after a second consent check inside postTrackEvent.
  postTrackEvent({
    type: 'session',
    payload: {
      utm_source: attribution.utm_source,
      utm_medium: attribution.utm_medium,
      utm_campaign: attribution.utm_campaign,
      utm_content: attribution.utm_content,
      utm_term: attribution.utm_term,
      landing_page: attribution.landing_page,
      referrer: attribution.referrer,
      referrer_domain: attribution.referrer_domain,
      device_type: attribution.device_type,
      landing_variant: attribution.landing_variant,
    },
  });

  // Fire landing page view funnel event
  trackFunnelEvent('landing_page_view');
}

/**
 * Save attribution data to Supabase after signup.
 *
 * Idempotent — if attribution already exists for this user, it's a no-op.
 */
export async function saveAttribution(userId: string): Promise<void> {
  const stored = getStoredAttribution();

  // Referral linking is account signup, not analytics. It does not read vidi_aid.
  // Claim before the attribution upsert so a consent withdrawal during that
  // await still links the referral.
  if (stored?.referral_code) {
    void supabase.functions.invoke('claim-referral').catch(() => {});
  }

  // Analytics rows and anonymous-id stitching require analytics consent.
  // Essential signup (the account itself, and a referral claim) is unchanged.
  if (!hasAnalyticsConsent()) return;

  if (!stored) {
    const anonymousId = getAnonymousId();
    if (!anonymousId) return;

    const { error } = await supabase.from('user_attribution').upsert(
      {
        user_id: userId,
        utm_source: null,
        landing_page: window.location.pathname,
        first_seen_at: new Date().toISOString(),
        signed_up_at: new Date().toISOString(),
        anonymous_id: anonymousId,
        device_type: getDeviceType(),
        referrer_domain: getReferrerDomain(),
        landing_variant: null,
      },
      { onConflict: 'user_id', ignoreDuplicates: true },
    );

    // Consent can be withdrawn while the upsert is in flight. Do not link
    // earlier anonymous analytics or emit signup_completed after that.
    if (!hasAnalyticsConsent()) return;

    if (error && error.code !== '23505') {
      reportError(error.message, { operation: 'saveAttribution' });
    }

    backfillFunnelUser(userId, anonymousId);
    trackFunnelEvent('signup_completed');

    return;
  }

  const { error } = await supabase.from('user_attribution').upsert(
    {
      user_id: userId,
      utm_source: stored.utm_source,
      utm_medium: stored.utm_medium,
      utm_campaign: stored.utm_campaign,
      utm_content: stored.utm_content,
      utm_term: stored.utm_term,
      referrer: stored.referrer,
      landing_page: stored.landing_page,
      first_seen_at: stored.first_seen_at,
      signed_up_at: new Date().toISOString(),
      referral_code: stored.referral_code,
      anonymous_id: stored.anonymous_id,
      device_type: stored.device_type,
      referrer_domain: stored.referrer_domain,
      landing_variant: stored.landing_variant,
    },
    { onConflict: 'user_id', ignoreDuplicates: true },
  );

  if (!hasAnalyticsConsent()) return;

  if (error && error.code !== '23505') {
    reportError(error.message, { operation: 'saveAttribution' });
  }

  backfillFunnelUser(userId, stored.anonymous_id);
  trackFunnelEvent('signup_completed');

  removeStoredRaw();
}

function backfillFunnelUser(userId: string, anonymousId: string): void {
  void supabase
    .from('funnel_events')
    .update({ user_id: userId })
    .eq('anonymous_id', anonymousId)
    .is('user_id', null)
    .then(
      () => {},
      () => {},
    );
}

/**
 * Read the currently stored attribution data.
 */
export function getStoredAttribution(): AttributionData | null {
  const stored = readStoredRaw();
  if (!stored) return null;
  try {
    return JSON.parse(stored) as AttributionData;
  } catch {
    return null;
  }
}

// --------------------------------------------------------------------------
// Funnel Event Tracking
// --------------------------------------------------------------------------

/**
 * Fire-and-forget POST to track-event.
 *
 * Analytics consent gates the whole send. Undecided or declined consent
 * does not read the session or vidi_aid. Consent is checked again after
 * session lookup, immediately before dispatch, so a revocation during
 * getSession cannot attach the token. The body never carries user_id.
 */
function postTrackEvent(body: { type: string; payload: Record<string, unknown> }): void {
  try {
    if (!hasAnalyticsConsent()) return;

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    void (async () => {
      if (!hasAnalyticsConsent()) return;

      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      try {
        const { data } = await supabase.auth.getSession();
        const token = data.session?.access_token;
        if (typeof token === 'string' && token.length > 0) {
          headers.Authorization = `Bearer ${token}`;
        }
      } catch {
        // Session lookup failed. A consented event can still go out anonymously.
      }

      if (!hasAnalyticsConsent()) return;
      const anonymousId = getAnonymousId();
      if (!anonymousId) return;

      await fetch(`${supabaseUrl}/functions/v1/track-event`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ...body, anonymous_id: anonymousId }),
      });
    })().catch(() => {});
  } catch {
    /* fire-and-forget */
  }
}

/**
 * Track a funnel event. Fire-and-forget — never blocks UI.
 * No-ops unless analytics consent is on. Does not send a caller-supplied user_id.
 * A consented session access token is sent as Authorization. With consent but
 * no session, the event is anonymous (user_id null on the server).
 */
export function trackFunnelEvent(eventType: string, metadata?: Record<string, unknown>): void {
  try {
    postTrackEvent({
      type: 'event',
      payload: {
        event_type: eventType,
        event_metadata: metadata ?? null,
      },
    });
  } catch {
    /* fire-and-forget */
  }
}

/**
 * Set the landing page variant (e.g. A/B test variant).
 * Updates sessionStorage and the session record.
 */
export function setLandingVariant(variant: string): void {
  if (!hasAnalyticsConsent()) return;

  const stored = getStoredAttribution();
  if (stored) {
    stored.landing_variant = variant;
    writeStoredRaw(JSON.stringify(stored));
  }
  postTrackEvent({
    type: 'session',
    payload: { landing_variant: variant },
  });
}

// --------------------------------------------------------------------------
// Helpers
// --------------------------------------------------------------------------

function inferSourceFromReferrer(referrer: string): string {
  const hostname = new URL(referrer).hostname.toLowerCase();

  const sourceMap: Record<string, string> = {
    'google.com': 'google',
    'www.google.com': 'google',
    'google.de': 'google',
    'www.google.de': 'google',
    'bing.com': 'bing',
    'www.bing.com': 'bing',
    'duckduckgo.com': 'duckduckgo',
    'reddit.com': 'reddit',
    'www.reddit.com': 'reddit',
    'old.reddit.com': 'reddit',
    't.co': 'twitter',
    'x.com': 'twitter',
    'twitter.com': 'twitter',
    'facebook.com': 'facebook',
    'www.facebook.com': 'facebook',
    'l.facebook.com': 'facebook',
    'youtube.com': 'youtube',
    'www.youtube.com': 'youtube',
    'linkedin.com': 'linkedin',
    'www.linkedin.com': 'linkedin',
    'discord.com': 'discord',
    'discord.gg': 'discord',
  };

  if (sourceMap[hostname]) return sourceMap[hostname];
  for (const [domain, source] of Object.entries(sourceMap)) {
    if (hostname.endsWith('.' + domain) || hostname === domain) return source;
  }
  return hostname;
}

// --------------------------------------------------------------------------
// UTM Link Builder
// --------------------------------------------------------------------------

export function buildUtmLink(
  path: string,
  params: { source: string; medium: string; campaign: string; content?: string; term?: string },
  baseUrl: string = 'https://vidilearn.com',
): string {
  const url = new URL(path, baseUrl);
  url.searchParams.set('utm_source', params.source);
  url.searchParams.set('utm_medium', params.medium);
  url.searchParams.set('utm_campaign', params.campaign);
  if (params.content) url.searchParams.set('utm_content', params.content);
  if (params.term) url.searchParams.set('utm_term', params.term);
  return url.toString();
}
